import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../../firebase";
import { showAlert } from "../../alertService";
import { Heading } from "../heading";
import { Button } from "../button";
import { Input } from "../input";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";

const BRAND_CTA =
  "inline-flex items-center gap-2 rounded-full bg-[#1b4d2e] px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#153d24] disabled:opacity-60";
const PILL_GROUP = "inline-flex items-center gap-1 rounded-full bg-gray-100 p-1";
const PILL_IDLE =
  "rounded-full px-3 py-1 text-sm text-gray-600 transition hover:text-[#1b4d2e]";
const PILL_ACTIVE =
  "rounded-full bg-[#1b4d2e] px-3 py-1 text-sm font-semibold text-white shadow-sm";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("es-MX", {
    // Zona fija: el post se fecha en hora de México. Sin esto el servidor
    // formatea en UTC y el navegador en la zona del visitante, así que un
    // publishedAt cercano a medianoche muestra días distintos en cada uno
    // (y en un client component eso es hydration mismatch).
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Un post "published" con publishedAt en el futuro NO se ve en el sitio
// todavía (el backend lista solo publishedAt <= ahora): la lista debe
// decirlo, o parece que publicar no funcionó.
function estaProgramado(p) {
  if (p.status !== "published" || !p.publishedAt) return false;
  const d = p.publishedAt.toDate ? p.publishedAt.toDate() : new Date(p.publishedAt);
  return d.getTime() > Date.now();
}

export default function BlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLang, setFilterLang] = useState("all");
  const [search, setSearch] = useState("");

  async function load() {
    setLoading(true);
    try {
      const q = query(collection(db, "blogPosts"), orderBy("updatedAt", "desc"));
      const snap = await getDocs(q);
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      showAlert("Error cargando los posts", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(post) {
    const title = post.translations?.es?.title || post.translations?.en?.title || post.id;
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, "blogPosts", post.id));
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      showAlert("Post eliminado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error eliminando el post", "error");
    }
  }

  const filteredPosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterLang === "es" && !p.translations?.es) return false;
      if (filterLang === "en" && !p.translations?.en) return false;
      if (q) {
        const haystack = [
          p.translations?.es?.title,
          p.translations?.en?.title,
          p.slugEs,
          p.slugEn,
          ...(p.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [posts, search, filterStatus, filterLang]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Heading level={1}>Blog</Heading>
        <button
          type="button"
          onClick={() => navigate("/dashboard/blog/new")}
          className={BRAND_CTA}
        >
          <PlusIcon className="h-4 w-4" />
          Nuevo post
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="max-w-xs flex-1">
          <Input
            placeholder="Buscar por título, slug, tag..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={PILL_GROUP}>
          {[
            { v: "all", label: "Todos" },
            { v: "published", label: "Publicados" },
            { v: "draft", label: "Borradores" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilterStatus(f.v)}
              className={filterStatus === f.v ? PILL_ACTIVE : PILL_IDLE}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className={PILL_GROUP}>
          {[
            { v: "all", label: "Ambos" },
            { v: "es", label: "ES" },
            { v: "en", label: "EN" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilterLang(f.v)}
              className={filterLang === f.v ? PILL_ACTIVE : PILL_IDLE}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Cargando...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-500">
          No hay posts todavía.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Idiomas</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Actualizado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPosts.map((p) => {
                const title =
                  p.translations?.es?.title ||
                  p.translations?.en?.title ||
                  "(sin título)";
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/blog/${p.id}`)}
                        className="text-left hover:underline"
                      >
                        {title}
                      </button>
                      {(p.slugEs || p.slugEn) && (
                        <div className="mt-0.5 text-xs text-gray-400">
                          /{p.slugEs || p.slugEn}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {p.translations?.es && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">
                            ES
                          </span>
                        )}
                        {p.translations?.en && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">
                            EN
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-[#1b4d2e]/10 px-2 py-0.5 text-xs text-[#1b4d2e]"
                          >
                            {tag}
                          </span>
                        ))}
                        {p.tags && p.tags.length > 3 && (
                          <span className="text-xs text-gray-400">
                            +{p.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {estaProgramado(p) ? (
                        <span
                          className="rounded-full px-2 py-0.5 text-xs bg-blue-100 text-blue-800"
                          title="Aún no se ve en el sitio: se publica solo en esa fecha"
                        >
                          Programado · {formatDate(p.publishedAt)}
                        </span>
                      ) : (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            p.status === "published"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                          title={p.status === "published" && p.publishedAt ? `Publicado el ${formatDate(p.publishedAt)}` : undefined}
                        >
                          {p.status === "published" ? "Publicado" : "Borrador"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(p.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          plain
                          onClick={() => navigate(`/dashboard/blog/${p.id}`)}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </Button>
                        <Button plain onClick={() => handleDelete(p)}>
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
